<template>
  <div class="mc-editable-table">
    <div class="d-flex justify-content-between align-items-center">
      <div class="fw-bold text-nowrap card-title mb-0">
        {{ tableTitle }}
      </div>
      <div class="ml-auto d-flex justify-content-end">
        <!-- To use custom button on top of the table use the scoped slot "actions" -->
        <!-- but you need to implement the add and delete button on the parent too -->
        <!-- these `props` are passed to the scoped slot -->
        <!-- there are probably better ways to do this, PDD/YPJ first -->
        <template v-if="$scopedSlots.actions">
          <slot
            name="actions"
            :handleAdd="handleAdd"
            :getData="getData"
            :handleSortChange="handleSortChange"
            :handleUnselectAll="handleUnselectAll"
            :deleteModal="$refs['mc-modal-delete']"
            :selectedIds="selectedIds"
            :disabledGlobal="disabledGlobal"
            :selectedIdsLength="selectedIdsLength"
          />
        </template>

        <!-- Show default button if no custom button is provided -->
        <template v-else>
          <McButton
            v-if="selectedIdsLength && insideCard"
            id="mc-delete-btn"
            variant="secondary-red"
            class="mx-2"
            :text="$t('global.delete')"
            icon="icon-outline_trash"
            @click="$refs['mc-modal-delete'].show()"
          />

          <McButton
            v-if="insideCard"
            variant="primary"
            :disabled="disabledGlobal"
            :text="$t('global.addField')"
            icon="icon-fill_plus"
            @click="handleAdd"
          />
        </template>
      </div>
    </div>

    <div :class="{ 'card p-3 mt-2': withCard }">
      <div v-if="hasEmptyState && !isBusy && items.length === 0">
        <slot name="emptyState">
          <McEmptyState
            :title="emptyStateTitle"
            :desc="emptyStateDesc"
            :buttonText="emptyStateButtonText"
            @buttonPrimaryAction="handleAdd"
          />
        </slot>
      </div>

      <template v-else>
        <b-row align-w="end" align-h="end">
          <template v-if="$scopedSlots.totalItems">
            <slot name="totalItems"> </slot>
          </template>
          <template v-else-if="insideCard">
            <b-col md="8" xl="10" order="1">
              <h6 v-if="selectedIdsLength" class="mc-row-check mt-0">
                <span class="badge btn-success">{{ selectedIdsLength }}</span>
                {{ $t('global.object.selectedRow') }}
                <a class="color-primary" @click="handleUnselectAll">{{
                  $t('global.action.clearAll')
                }}</a>
              </h6>
            </b-col>
          </template>
          <template v-if="$scopedSlots.search">
            <slot
              name="search"
              :search="search"
              :selectedIds="selectedIds"
              :selectedIdsLength="selectedIdsLength"
              :handleSearchChange="handleSearchChange"
              :isBusy="isBusy"
              :handleUnselectAll="handleUnselectAll"
            ></slot>
          </template>
          <template v-else>
            <b-col
              v-if="searchable"
              md="4"
              xl="2"
              :class="{ 'mt-0': withCard, 'mt-2': !withCard }"
              order="2"
            >
              <b-input-group size="sm" class="mc-has-search" @click="focusSearch">
                <template #prepend>
                  <i class="ps-2 mc-icon icon-fill_search"></i>
                </template>
                <b-form-input
                  ref="searchInput"
                  :placeholder="$t('global.search') + '...'"
                  :disabled="disabledGlobal"
                  :value="search"
                  @input="handleSearchChange"
                />
                <template v-if="search" #append>
                  <button
                    class="clear-search-btn"
                    :disabled="disabledGlobal"
                    @click="handleClearSearch"
                  >
                    <i class="pe-2 mc-icon icon-outline_close"></i>
                  </button>
                </template>
              </b-input-group>
            </b-col>
          </template>
        </b-row>
        <FormulateForm
          ref="formRef"
          v-model="formValues"
          name="editable-table-form"
          :errors="formErrors"
          @submit="handleSubmit"
          @failed-validation="handleFailedValidation"
        >
          <div class="table-responsive my-3 border border-1 mc-table-overflow">
            <b-table
              :busy="isBusy"
              :items="items"
              :fields="computedFields"
              :per-page="limit"
              fixed
              show-empty
              class="editable-table"
            >
              <!-- Loading -->
              <template #table-busy>
                <div class="__busy">
                  <b-spinner class="align-middle mb-2" />
                  <strong>{{ $t('global.text.loading') }}...</strong>
                </div>
              </template>

              <!-- Empty Result -->
              <template #empty>
                <div class="text-center my-4">
                  {{ $t('global.emptyTable') }}
                </div>
              </template>

              <!-- Slot: Header Checkbox -->
              <template #head(checkbox)>
                <div class="form-check" style="width: 10px">
                  <b-form-checkbox
                    v-model="selectedAll"
                    :disabled="disabledGlobal"
                    @change="handleSelectAll"
                  />
                </div>
              </template>

              <!-- Slot: Header Data -->
              <template #head()="data">
                <div @click="handleSortChange(data)">
                  {{ $t(data.label) }}
                  <template v-if="data.field.isSortable">
                    <div v-if="showSortIcon(data, 'asc')" class="sort-icon-up">
                      <i class="mc-icon icon-outline_chevron-up"></i>
                    </div>
                    <div v-if="showSortIcon(data, 'desc')" class="sort-icon-down">
                      <i class="mc-icon icon-outline_chevron-down"></i>
                    </div>
                  </template>
                </div>
              </template>

              <!-- Slot: Cell Checkbox -->
              <template #cell(checkbox)="data">
                <div class="form-check">
                  <b-form-checkbox
                    :checked="selectedIds[data.item.id]"
                    :disabled="disabledGlobal"
                    @change="(event) => handleSelect(event, data)"
                  />
                </div>
              </template>

              <!-- Slot: All Cells -->
              <template #cell()="{ index, field, value, item }">
                <template v-if="(field.editable || field.isEditable) && isEditingRow == index">
                  <FormulateInput
                    v-if="field.type === 'phone-number'"
                    v-bind="{ ...field }"
                    :ref="`${field.key}-ref`"
                    :rowItem="item"
                    :label="null"
                    :type="field.type || 'text'"
                    :value="getInputInitialValue({ field, item })"
                    :disabled="isSubmitting || field.disabled"
                    size="sm"
                    validation-behavior="live"
                    error-behavior="live"
                    @countryCode="getCountryCode"
                  />
                  <FormulateInput
                    v-else
                    v-bind="{ ...field }"
                    :ref="`${field.key}-ref`"
                    :rowItem="item"
                    :label="null"
                    :placeholder="$t(field.placeholder)"
                    :type="field.type || 'text'"
                    :value="getInputInitialValue({ field, item })"
                    :disabled="isSubmitting || field.disabled"
                    :initialObject="field.getInitialObject?.(item)"
                    :fullObject="fullObject?.[field.name]"
                    size="sm"
                    validation-behavior="live"
                    error-behavior="live"
                    @update:fullObject="($event) => (fullObject[field.name] = $event)"
                  />
                </template>

                <!-- Bold First Column -->
                <template v-else-if="field.isRedirectable">
                  <div class="fw-bold text-primary">
                    {{ value }}
                  </div>
                </template>

                <template v-else-if="field.customColumn && isEditingRow !== index">
                  <slot :name="field.key" v-bind="{ index, field, value, item }"></slot>
                </template>

                <template v-else-if="field.type === 'file-input' && isEditingRow !== index">
                  <div v-if="item && item[field.getFile.urlKey]" class="mc-file-preview-cell">
                    <div
                      class="mc-file-preview-img-wrapper"
                      @click="handleFileDownload(item[field.getFile.urlKey])"
                    >
                      <img
                        v-if="isImageFile(item[field.getFile.nameKey])"
                        :src="item[field.getFile.urlKey]"
                        :alt="item[field.getFile.nameKey]"
                      />

                      <div v-else class="mc-file-preview-icon">
                        <i class="icon-outline_file-text"></i>
                      </div>

                      <div class="mc-file-preview-download-overlay">
                        <i class="icon-outline_download"></i>
                      </div>
                    </div>

                    <span class="mc-file-preview-name-text">
                      {{ item[field.getFile.nameKey] }}
                    </span>
                  </div>

                  <span v-else>-</span>
                </template>

                <template v-else-if="isEditingRow == index">
                  <slot :name="`${field.key}-edit`" v-bind="{ index, field, value, item }"></slot>
                </template>

                <template v-else>
                  {{ transformCellValue(value, field) }}
                </template>
              </template>

              <!-- Slot: Action Column (Last Column) -->
              <template #cell(action)="data">
                <div v-if="isEditingRow == data.index">
                  <b-button variant="outline" class="__accept-button btn" type="submit">
                    <i class="mc-icon icon-fill_checkmark"></i>
                  </b-button>

                  <b-button
                    variant="outline"
                    class="__cancel-button btn"
                    @click="handleEditCancel(data)"
                  >
                    <i class="mc-icon icon-outline_close"></i>
                  </b-button>
                </div>

                <b-button
                  v-else
                  variant="outline"
                  :class="{
                    '__edit-button btn': true,
                    __disabled: disabled || isEditingRow !== null || isBusy,
                  }"
                  @click="handleEditStart(data)"
                >
                  <i class="mc-icon icon-outline_edit"></i>
                </b-button>
              </template>

              <!-- Slot: Slot override from parent component -->
              <template v-for="(index, name) in $scopedSlots" #[name]="data">
                <slot :name="name" v-bind="data"></slot>
              </template>
            </b-table>
          </div>
        </FormulateForm>
        <div class="d-flex flex-wrap flex-md-nowrap">
          <div class="mc-limit">
            <div class="__limit">
              <div>{{ $t('global.show') }}</div>
              <b-form-select
                class="form-select form-select-sm __limit--dropdown"
                size="sm"
                :disabled="disabledGlobal"
                :options="limitOptions"
                :value="limit"
                @input="handleLimitChange"
              />
              <div>{{ $t('global.text.data') }}</div>
            </div>

            <div v-if="totalCount === 0" class="__info">
              <strong>{{ totalCount }}</strong> {{ $t('global.object.of') }}
              <strong>{{ totalCount }}</strong>
              {{ $t('global.text.data') }}
            </div>

            <div v-else class="__info">
              <strong>{{ startData }}</strong> - <strong>{{ endData }}</strong>
              {{ $t('global.object.of') }} <strong>{{ totalCount }}</strong>
              {{ $t('global.text.data') }}
            </div>
          </div>
          <div class="mc-pagination-container">
            <b-pagination
              :disabled="disabledGlobal"
              :per-page="limit"
              :total-rows="totalCount"
              :value="page"
              @change="handlePageChange"
            />
          </div>
        </div>
      </template>
    </div>
    <b-modal
      id="mc-modal-delete"
      ref="mc-modal-delete"
      class="mc-modal"
      :title="$t('global.action.delete')"
      size="md"
      centered
      scrollable
    >
      <div class="card-body p-2">
        <p>
          {{ $t('global.confirmDelete1') }} <strong>{{ selectedIdsLength }}</strong>
          {{ $t('global.confirmDelete2') }}
        </p>
      </div>
      <template #modal-footer="{ cancel }">
        <div class="d-flex">
          <McButton
            variant="secondary"
            class="me-2"
            :text="$t('global.action.cancel')"
            @click="cancel()"
          />
          <McButton variant="primary" :text="$t('global.action.delete')" @click="handleDelete" />
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { debounce } from '@/helpers/debounce'
import McButton from '@/components/McButton'
import { formatNumber } from '@/helpers/format-number'
import McEmptyState from '@/components/McEmptyState'

export default {
  components: {
    McButton,
    McEmptyState,
  },
  props: {
    fields: { required: true },
    apiClient: { required: true },
    limitOptions: {
      default: () => [10, 25, 50, 100],
    },
    searchable: { type: Boolean },
    busy: { type: Boolean },
    tableTitle: { type: String },
    withCard: { type: Boolean },
    disabled: { type: Boolean },
    showNoData: { type: Boolean },
    apiErrorMappings: { type: Object, default: undefined },
    'hasEmit:submitError': { type: Boolean, default: false },
    hasBulkDelete: { type: Boolean, default: false },
    selectEvent: { type: Function, default: (items) => {} },
    editNeedAllData: { type: Boolean, default: false },
    cancelEditNeedRefetch: { type: Boolean, default: false },
    insideCard: { type: Boolean, default: true },
    hasEmptyState: { type: Boolean, default: false },
    emptyStateTitle: { type: String, default: 'No data available' },
    emptyStateDesc: { type: String, default: '' },
    emptyStateButtonText: { type: String, default: 'Add Data' },
    editStartFocusKey: { type: String, default: null },
  },
  emits: ['submitError'],
  data() {
    return {
      page: 1,
      limit: this.limitOptions[0] ?? 10,
      totalCount: 0,
      isBusy: true,
      search: '',
      sorts: [],
      items: [],
      ids: [],
      rowUpdate: {},
      isEditingRow: null,
      isAdding: false,
      isSubmitting: false,
      selectedIds: {},
      selectedIdsLength: 0,
      selectedAll: false,
      formValues: {},
      initialFormValues: {},

      fullObject: {},

      formErrors: {},
      invalidFormValues: {},
    }
  },
  computed: {
    disabledGlobal() {
      return this.disabled || this.isEditingRow !== null || this.isBusy
    },
    startData() {
      return this.limit * this.page - this.limit + 1
    },
    endData() {
      const _endData = this.limit * this.page
      return this.totalCount < _endData ? this.totalCount : _endData
    },
    // Fields with added defaults
    computedFields() {
      return [
        {
          key: 'checkbox',
          label: '',
        },
        ...this.fields,
        {
          key: 'action',
          label: '',
          thClass: 'mc-action-col-head',
          tdClass: 'mc-action-cell',
        },
      ]
    },
    errorKeys() {
      return Object.keys(this.formErrors)
    },
  },
  watch: {
    formValues() {
      if (this.errorKeys.length === 0) return

      this.errorKeys.forEach((errorKey) => {
        if (this.invalidFormValues[errorKey] != this.formValues[errorKey]) {
          // delete the field from the state to show errors
          this.$delete(this.formErrors, errorKey)
          this.$delete(this.invalidFormValues, errorKey)
        }
      })

      if (this.errorKeys.length === 0) {
        this.resetErrors()
      }
    },
  },
  async mounted() {
    if (this.showNoData) {
      this.isBusy = false
      return
    }

    await this.$nextTick()
    await this.getData({
      limit: this.limit,
      page: this.page,
    })
  },
  methods: {
    focusSearch() {
      this.$refs.searchInput.focus()
    },
    transformCellValue(value, field) {
      if (value === false) return ''
      if (typeof value === 'number') return formatNumber(value)
      if (field?.type === 'file-input' && typeof value === 'string') {
        return value.split('/').pop()
      }
      return value
    },
    getInputInitialValue({ item, field }) {
      // get value from callback in field setting
      // can be used for formatting select input
      if (field.customValueFn) {
        return field.customValueFn(item) || false
      }

      // get value from item
      if (typeof item[field.key] !== 'undefined') {
        return item[field.key]
      }

      return false
    },
    showSortIcon(data, order) {
      const currentColumnSort = this.sorts.find((sort) => sort.key === data.field.key)
      if (!currentColumnSort) {
        return true
      }

      return currentColumnSort.key === data.field.key && currentColumnSort.order === order
    },
    // TECHDEBT: Only supports resourceful format
    // getData methods can be improved to support user-supplied data
    async getData(opts) {
      if (!this.apiClient?.get) {
        this.isBusy = false
        return
      }

      this.isBusy = true
      const res = await this.apiClient.get(opts)
      if (res.errors) {
        this.isBusy = false
        return
      }
      this.items = res.data.paginated_result
      this.ids = res.data.ids
      this.page = res.metadata.page
      this.totalCount = res.metadata.total_count

      if (res.data.paginated_result.length === 0 && !this.search) {
        this.$emit('available-change', false)
      }

      this.isBusy = false
    },
    handleSearchChange: debounce(async function (_search) {
      this.search = _search
      const opts = {
        limit: this.limit,
        search: _search != '' ? _search : undefined,
        page: 1,
        sorts: this.sorts.map((sort) => `${sort.sortKey || sort.key} ${sort.order}`),
      }
      await this.getData(opts)
      this.$refs['searchInput'].$el.focus()
    }, 500),
    handleClearSearch() {
      this.search = ''
      this.handleSearchChange(undefined)
    },
    async handleLimitChange(_limit) {
      this.limit = _limit

      await this.getData({
        limit: _limit,
        search: this.search != '' ? this.search : undefined,
        page: 1,
        sorts: this.sorts.map((sort) => `${sort.sortKey || sort.key} ${sort.order}`),
      })
    },
    async handlePageChange(newPage) {
      const opts = {
        page: newPage,
        search: this.search != '' ? this.search : undefined,
        limit: this.limit,
        sorts: this.sorts.map((sort) => `${sort.sortKey || sort.key} ${sort.order}`),
      }
      await this.getData(opts)
    },
    async handleSortChange(data) {
      // If this function is called without data params,
      // remove sorting (revert to default)
      if (!data) {
        this.sorts = []
        return
      }

      // If field not sortable or If currently editing a field, disable sort
      if (!data?.field?.isSortable) return
      if (this.isEditingRow !== null) return

      // Get index of current column sort, if any
      const currentColumnSortIndex = this.sorts.findIndex((sort) => sort.key === data.field.key)
      // Get the sorting data (key, order)
      const currentColumnSort = this.sorts[currentColumnSortIndex]
      const newSorts = [...this.sorts]
      // This switch case is used to toggle between these conditions:
      // default - asc - desc - default (repeat)
      switch (currentColumnSort?.order) {
        case 'asc':
          newSorts[currentColumnSortIndex] = {
            key: currentColumnSort.key,
            sortKey: currentColumnSort.sortKey,
            order: 'desc',
          }
          break

        case 'desc':
          newSorts.splice(currentColumnSortIndex, 1)
          break

        default:
          newSorts.push({
            key: data.field.key,
            sortKey: data.field.sortKey,
            order: 'asc',
          })
          break
      }

      this.sorts = [...newSorts]
      await this.$nextTick()

      await this.getData({
        limit: this.limit,
        page: 1,
        search: this.search != '' ? this.search : undefined,
        sorts: this.sorts.map((sort) => `${sort.sortKey || sort.key} ${sort.order}`),
      })
    },
    async handleEditStart(data) {
      if (this.selectedIdsLength) this.handleUnselectAll()
      if (this.isEditingRow != null) return
      // refetch single data from backend for data integrity
      const refetchedData = await this.apiClient.show(data.item.id)
      if (refetchedData.errors) {
        return
      }

      // update the refetched data (row) in the table
      // map is used since 'this.computedItems[index] = {}' will not work
      this.items = this.items.map((item) => {
        if (item.id == data.item.id) return refetchedData.data
        return item
      })

      this.fields.map((field) => {
        if (field?.fullObjectKey && field?.name) {
          this.$set(this.fullObject, field.name, refetchedData.data?.[field.fullObjectKey])
        } else if (field?.getInitialObject && field?.name) {
          this.$set(this.fullObject, field.name, field.getInitialObject(refetchedData.data))
        }
      })

      this.isEditingRow = data.index
      await this.$nextTick()

      this.initialFormValues = { ...this.formValues }

      const fallbackFocusFieldKey = this.fields[0]?.key
      const focusFieldKey =
        this.fields.find((field) => field.key === this.editStartFocusKey)?.key ||
        fallbackFocusFieldKey
      this.$refs[`${focusFieldKey}-ref`]?.$el?.querySelector('input')?.focus()
    },
    async handleEditCancel(data) {
      if (this.isSubmitting) return
      if (this.isAdding) {
        const [, ...newItems] = this.items
        this.items = newItems
        this.isAdding = false
      } else if (!this.isAdding && this.cancelEditNeedRefetch) {
        // refetch single data from backend for data integrity
        const refetchedData = await this.apiClient.show(data.item.id)
        if (refetchedData.errors) {
          return
        }

        // update the refetched data (row) in the table
        // map is used since 'this.computedItems[index] = {}' will not work
        this.items = this.items.map((item) => {
          if (item.id == data.item.id) return refetchedData.data
          return item
        })
      }

      this.resetErrors()

      this.initialFormValues = {}
      this.isEditingRow = null
    },
    // TECHDEBT: transformFields will be on formulate input instead of here
    transformFields(formValues, fields) {
      const formValuesKeys = Object.keys(formValues)
      const validKeys = formValuesKeys.filter((key) => formValues[key] !== false)

      return Object.assign(
        {},
        ...validKeys.map((key) => {
          const currentField = fields.find((item) => item.key == key)
          const value = formValues[key]

          if (currentField && currentField.type === 'file-input' && value) {
            const nameKey = currentField.storeFile?.nameKey
            const base64Key = currentField.storeFile?.base64Key

            if (nameKey && base64Key) {
              return {
                [nameKey]: value.file_name,
                [base64Key]: value.file_base64,
              }
            }
          }

          if (currentField && currentField._type === 'number') {
            const isValid = Boolean(value) || value === '0' || value === 0
            return { [key]: isValid ? Number(value) : null }
          }

          if (currentField && currentField.type === 'text') {
            return { [key]: formValues[key].trim().replace(/  +/g, ' ') }
          }

          return { [key]: value }
        })
      )
    },
    async handleSubmit(data) {
      if (this.isSubmitting) {
        this.$mcToast.warning('Mohon tunggu')
        return
      }

      this.isSubmitting = true

      // Cast fields to correct types
      const transformedData = this.transformFields(data, this.fields)

      let newData = null

      if (this.isAdding) {
        newData = await this.submitStore(transformedData)
      } else {
        newData = await this.submitUpdate(transformedData, this.items[this.isEditingRow])
      }

      if (newData.errors) {
        this.isSubmitting = false

        this.assignErrors(newData)
        return
      }

      // Replace data on the table
      if (newData !== null) {
        this.items.splice(this.isEditingRow, 1, newData)
      }

      this.initialFormValues = {}
      this.isEditingRow = null
      this.isAdding = false
      this.isSubmitting = false
    },
    async submitStore(data) {
      const createdData = await this.apiClient.store(data)
      if (createdData.errors) return createdData

      const refetchedData = await this.apiClient.show(createdData.data.id)
      if (refetchedData.errors) return refetchedData

      this.ids.push(refetchedData.data.id)
      this.totalCount += 1

      this.$mcToast.success('Data berhasil ditambahkan')

      return {
        ...data,
        ...refetchedData.data,
      }
    },
    async submitUpdate(data, oldData) {
      let patchData = {},
        hasChanged = false
      if (!this.editNeedAllData) {
        const filterRes = this.filterUnchangedData(data, this.initialFormValues)
        patchData = filterRes[0]
        hasChanged = filterRes[1]
        if (!hasChanged) return oldData
      } else {
        patchData = data
      }

      const updatedData = await this.apiClient.update(oldData.id, patchData)
      if (updatedData.errors) return updatedData

      const refetchedData = await this.apiClient.show(oldData.id)
      if (refetchedData.errors) return refetchedData

      this.$mcToast.success('Data berhasil diubah')

      return {
        ...data,
        ...refetchedData.data,
      }
    },
    // eslint-disable-next-line no-unused-vars
    handleFailedValidation(_errors) {
      return this.$mcToast.error('Terdapat kesalahan format pada input anda')
    },
    filterUnchangedData(newValue, oldValue) {
      const keys = new Set([...Object.keys(newValue), ...Object.keys(oldValue)])
      const _newValue = {}
      keys.forEach((key) => {
        if (key == 'id') return
        if (oldValue[key] == newValue[key]) return
        _newValue[key] = newValue[key]
      })

      const entries = Object.entries(_newValue)
      const hasChanged = entries.length > 0 && entries.every((value) => value)
      return [_newValue, hasChanged]
    },
    async handleDelete() {
      if (this.hasBulkDelete) {
        const ids = Object.keys(this.selectedIds).filter((key) => this.selectedIds[key])
        await this.apiClient.delete({ ids })
      } else {
        const promises = []
        for (let key in this.selectedIds) {
          if (this.selectedIds[key]) {
            promises.push(this.apiClient.delete(key))
          }
        }
        await Promise.all(promises)
      }

      if (this.search) this.handleClearSearch()
      await this.getData({
        limit: this.limit,
        page: 1,
      })
      this.handleSortChange()
      this.handleUnselectAll()
      this.$refs['mc-modal-delete'].hide()
      this.$mcToast.success('Data berhasil dihapus')
    },
    handleSelect(checked, data) {
      this.selectedIds = { ...this.selectedIds, [data.item.id]: checked }
      if (checked) {
        this.selectedIdsLength += 1
        if (this.selectedIdsLength == this.ids.length) this.selectedAll = true
      } else {
        this.selectedAll = false
        this.selectedIdsLength -= 1
      }
      this.selectEvent(this.selectedIds)
    },
    handleSelectAll(event) {
      if (event) {
        this.selectedIds = Object.fromEntries(this.ids.map((key) => [key, true]))
        this.selectedIdsLength = this.ids.length ?? 0
        this.selectedAll = true
      } else {
        this.handleUnselectAll()
      }
      this.selectEvent(this.selectedIds)
    },
    handleUnselectAll() {
      this.selectedIds = {}
      this.selectedAll = false
      this.selectedIdsLength = 0
    },
    handleAdd() {
      if (this.isEditingRow != null) return
      this.isEditingRow = 0
      this.isAdding = true

      this.$emit('available-change', true)

      this.items = [{ id: 0 }, ...this.items]
    },
    assignErrors(data) {
      if (data.status === 500) return

      if (data.status === 400) {
        const errorKeys = Object.keys(data.errors)

        // assign to form error
        errorKeys.forEach((errorKey) => {
          const errorMessage =
            this.apiErrorMappings?.[errorKey]?.[data.errors[errorKey][0]] ??
            'Terdapat kesalahan format pada input anda'
          this.$set(this.formErrors, errorKey, errorMessage)
        })

        this.invalidFormValues = { ...this.formValues }
      }

      if (this['hasEmit:submitError']) {
        this.$emit('submitError', data)
        return
      }

      this.$mcToast.error('Gagal simpan. Mohon periksa kembali data anda')
    },
    resetErrors() {
      this.invalidFormValues = {}
      this.formErrors = {}
    },

    getCountryCode(value) {
      this.$emit('countryCode', value)
    },
    isImageFile(fileName) {
      if (!fileName) return false
      const ext = fileName.split('.').pop().toLowerCase()
      return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)
    },
    handleFileDownload(url) {
      if (!url) return
      window.open(url, '_blank')
    },
    getFileNameFromUrl(url) {
      if (typeof url !== 'string') return ''
      return url.split('/').pop()
    },
  },
}
</script>

<style lang="scss" scoped>
@import './style.scss';
</style>
